'use client'

import { SeoLinkGroup } from '@/lib/generated/graphql-types'
import { getSalaryUrl } from '../filters/get-salary-filters-from-url'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { ChevronRight } from 'lucide-react'
import { SalaryFilterParam } from '../constants'
import { SeoLink } from './seo-link'
import { cn } from '@/lib/css-utils'
import { SeoLinkGroupType } from './constants'

function Dot() {
  return <span className="text-copy-pale text-xs flex items-center">•</span>
}

const MAX_LINKS_TO_SHOW = 14

export function SalarySeoLinkGroup({
  group,
  isAuthenticated,
}: {
  group: SeoLinkGroup
  isAuthenticated: boolean
}) {
  const [showMore, setShowMore] = useState(false)
  const showTheViewMoreButton = group.seoLinks.length > MAX_LINKS_TO_SHOW
  if (!group.seoLinks.length) return null

  return (
    <div className="md:grid md:grid-cols-8 lg:grid-cols-12 py-2">
      <h4 className="font-semibold text-sm text-copy-primary md:col-span-1">
        {group.groupName}
      </h4>
      <ul className="text-sm flex flex-wrap gap-2 md:col-span-7 lg:col-span-11">
        {group.seoLinks
          .sort((a, b) => {
            if (group.groupType === SeoLinkGroupType.location) {
              return a.state?.name.localeCompare(b.state?.name ?? '') ?? 0
            }
            if (group.groupType === SeoLinkGroupType.specialty) {
              return (
                a.occupation?.specialty?.fullName.localeCompare(
                  b.occupation?.specialty?.fullName ?? '',
                ) ?? 0
              )
            }
            return 0
          })
          .map((link, index) => {
            const isLast =
              showMore || !showTheViewMoreButton
                ? index === group.seoLinks.length - 1
                : index >= MAX_LINKS_TO_SHOW
            const salaryUrl = getSalaryUrl({
              filtersParams: {
                [SalaryFilterParam.jobType]: link.jobType,
                [SalaryFilterParam.location]: link.state?.slug,
                [SalaryFilterParam.occupation]:
                  link.occupation?.slug ?? link.profession?.slug,
              },
            })
            return (
              <li
                key={`${group.groupType}-${index}`}
                className={cn('flex items-center gap-2', {
                  hidden: !showMore && index >= MAX_LINKS_TO_SHOW,
                })}
              >
                <SeoLink
                  link={link}
                  salaryUrl={salaryUrl}
                  isAuthenticated={isAuthenticated}
                  group={group}
                  data-testid="seo-link"
                />
                {isLast ? null : <Dot />}
              </li>
            )
          })}
        {showTheViewMoreButton ? (
          <li>
            <Button
              onClick={() => setShowMore(!showMore)}
              className="w-full justify-start pl-1 p-0"
              variant="link"
            >
              {showMore ? (
                'Show Less'
              ) : (
                <>
                  Show More
                  <ChevronRight className="w-5 h-5" />
                </>
              )}
            </Button>
          </li>
        ) : null}
      </ul>
    </div>
  )
}
